/**
 * Number, date, and label formatting for the client. One place, so a week reads the same in a
 * tooltip, an axis, and a table cell.
 */

const HOURS = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const HOURS_EXACT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const WHOLE = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const PERCENT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 });

/** Hours, as the app says them: `128.5 h`. A dash for nothing, never `0` for unknown. */
export function hours(value, { exact = false, unit = true } = {}) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const text = exact ? HOURS_EXACT.format(n) : HOURS.format(n);
  return unit ? `${text} h` : text;
}

/** A ratio as a percentage: 1.15 → `115%`. */
export function percent(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return PERCENT.format(Number(value));
}

const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/**
 * Dollars, whole: `184500` → `$184,500`. A dash for nothing, because Total Billing distinguishes
 * "no value recorded" from a real zero, and `$0` is a claim the screen has to be able to make
 * (Estimate Tool 9.2: a zero is an absence, and the row says which).
 */
export function money(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return MONEY.format(Number(value));
}

export function count(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return WHOLE.format(Number(value));
}

/** `2026-09-14` → `Sep 14`. Parsed as a plain date, so no timezone shifts the day. */
export function shortDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  const month = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ][m - 1];
  return `${month} ${d}`;
}

/** The label a week column wears: `Sep 14` for the Monday that starts it. */
export const weekLabel = shortDate;

/**
 * `2026-09-14` → `Sep 14, 2026`. The form a table column wants when its rows span years:
 * `shortDate` would leave a 2017 project and a 2027 one looking the same, and `longDate`'s
 * weekday is noise in a cell nobody is reading for the day of the week.
 */
export function mediumDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return `${shortDate(iso)}, ${y}`;
}

/** `2026-09-14` → `Mon, Sep 14, 2026`, for tooltips and banners. */
export function longDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** How long ago a timestamp was, in the words a freshness banner uses. */
export function since(isoTimestamp, now = new Date()) {
  if (!isoTimestamp) return 'never';
  const then = new Date(isoTimestamp);
  if (Number.isNaN(then.getTime())) return 'unknown';
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hoursAgo = Math.floor(minutes / 60);
  if (hoursAgo < 24) return `${hoursAgo} h ago`;
  const days = Math.floor(hoursAgo / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** Title Case a canonical value for display when no label came with it. */
export function humanize(value) {
  if (!value) return '—';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
